import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label: "Et QR-kode-bordskilt bør være praktisk før det er dekorativt",
  },
  {
    id: "why",
    label: "Hvorfor er QR-kode-bordskilt nyttige for restauranter?",
  },
  {
    id: "formats",
    label: "Hvilke formater kan et QR-kode-bordskilt ha?",
  },
  {
    id: "design",
    label: "Hva bør stå på et QR-kode-bordskilt?",
  },
  {
    id: "placement",
    label: "Hvor bør et QR-kode-bordskilt plasseres?",
  },
  {
    id: "mistakes",
    label: "Hvilke feil skjer med QR-kode-bordskilt?",
  },
  {
    id: "recommendation",
    label: "Hvordan ville jeg startet som restauranteier?",
  },
  {
    id: "next-steps",
    label: "Hva bør du gjøre nå?",
  },
  {
    id: "bestplace",
    label: "Hvordan Best Place hjelper deg med å forberede QR-kode-bordskilt",
  },
  {
    id: "faq",
    label: "FAQ om QR-kode-bordskilt",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visuell idé">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idé til illustrasjon:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function RestaurantQrCodeTableTentNo() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title =
      "Restaurant QR-kode bordskilt: plasser digital meny riktig ved bordet | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          de: "/de/restaurant-qr-code-tischaufsteller",
          en: "/en/restaurant-qr-code-table-tent",
          no: "/no/restaurant-qr-kode-bordskilt",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Restaurant QR-kode bordskilt: plasser den digitale menyen riktig
              ved bordet
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Et QR-kode-bordskilt er ofte det mest synlige inngangspunktet
                til en digital meny. Gjesten ser det ved bordet, skanner koden
                og åpner menyen på smarttelefonen.
              </p>

              <p className="seoHero__text">
                Men et pent bordskilt er ikke nok. QR-koden må være stor nok,
                lett å se, tydelig forklart og koblet til en mobil meny som
                faktisk er oppdatert.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="no" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Innhold i artikkelen</h2>

        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>
            Et QR-kode-bordskilt bør være praktisk før det er dekorativt
          </h2>

          <p>
            Mange restauranter tenker først på design: farge, logo, materiale
            og form. Slike detaljer betyr noe, men de er ikke førsteprioritet.
            Førsteprioritet er om gjesten raskt kan se, forstå og skanne
            QR-koden.
          </p>

          <p>
            Et bordskilt må fungere i det ekte restaurantmiljøet: med ekte lys,
            ekte bordplassering, ekte smarttelefoner og ekte gjester som kanskje
            ikke vet hva som skjer etter skanningen.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Svakere</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">QR-koden er for liten.</li>
                <li className="text__seo-li">
                  Bordskiltet står på et dårlig sted.
                </li>
                <li className="text__seo-li">
                  Materialet reflekterer lys for sterkt.
                </li>
                <li className="text__seo-li">Teksten sier bare «Skann meg».</li>
                <li className="text__seo-li">
                  QR-koden åpner en PDF som er vanskelig å lese.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Bedre</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  QR-koden er stor og har god kontrast.
                </li>
                <li className="text__seo-li">
                  Bordskiltet er tydelig synlig.
                </li>
                <li className="text__seo-li">
                  Materialet er testet i restaurantens lys.
                </li>
                <li className="text__seo-li">Teksten forklarer fordelen.</li>
                <li className="text__seo-li">
                  QR-koden åpner en mobil menyside.
                </li>
              </ul>
            </div>
          </div>

          <h3>Test menyen først, produser bordskilt etterpå</h3>

          <p>
            Før du bestiller mange bordskilt, bør den digitale menyen fungere på
            smarttelefon, og QR-koden bør testes ved ekte bord. Slik unngår du å
            trykke pene materialer som ikke fungerer godt i daglig service.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvorfor er QR-kode-bordskilt nyttige for restauranter?</h2>

            <p>
              Et bordskilt er mer synlig enn et lite klistremerke og mer
              fleksibelt enn et fast skilt. Det kan stå på bord, ved disken,
              utebord, venteområder eller nær betalingspunktet.
            </p>

            <p>
              Det er spesielt nyttig når menyen endres regelmessig: dagens
              tilbud, drikke, sesongretter, utsolgte produkter eller
              prisendringer kan oppdateres online uten å trykke QR-koden på
              nytt.
            </p>

            <h3>Bordskiltet er et lite servicepunkt ved bordet</h3>

            <p>
              Bordskiltet er ikke bare et trykkprodukt. Det er et synlig
              inngangspunkt til restaurantens digitale meny. Hvis det er tydelig,
              stabilt og lett å skanne, kan det hjelpe gjester med å åpne menyen
              raskere og redusere gjentatte spørsmål til personalet.
            </p>

            <p>
              Kvaliteten avgjøres ikke bare av selve bordskiltet, men av hele
              flyten: plassering på bordet, QR-kodens størrelse, forklarende
              tekst, mobilvisning og oppdatert menyinnhold.
            </p>
          </div>
        </section>

        <section id="formats" className="acquaintance">
          <h2>Hvilke formater kan et QR-kode-bordskilt ha?</h2>

          <p>
            Restauranter kan velge mellom enkle og mer profesjonelle formater.
            For en første test er den dyreste løsningen ikke alltid nødvendig.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Laminert kort.</li>
            <li className="text__seo-li">Akrylholder.</li>
            <li className="text__seo-li">Papirholder.</li>
            <li className="text__seo-li">Holder av tre eller metall.</li>
            <li className="text__seo-li">3D-printet holder.</li>
            <li className="text__seo-li">Bordskilt med restaurantens merkevare.</li>
            <li className="text__seo-li">Dobbeltsidig bordskilt.</li>
            <li className="text__seo-li">Kombinert med dagens tilbud.</li>
          </ul>

          <p>
            Som første steg er et utskiftbart kort ofte bedre enn et fast
            klistremerke. Hvis tekst, design eller plassering ikke fungerer, kan
            kortet endres raskt.
          </p>

          <VisualIdea>
            Tre alternativer ved siden av hverandre: laminert kort,
            akrylholder og en 3D-printet holder med utskiftbart QR-kodekort.
          </VisualIdea>
        </section>

        <section id="design" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hva bør stå på et QR-kode-bordskilt?</h2>

            <p>
              Et godt bordskilt svarer umiddelbart på ett spørsmål: hva skjer
              hvis jeg skanner denne QR-koden?
            </p>

            <p>
              Derfor bør QR-koden støttes av en kort og tydelig tekst. Gjesten
              skal ikke måtte gjette om koden åpner en meny, bestillingsside,
              betalingsside eller anmeldelsesskjema.
            </p>

            <div className="seoCompareSmall">
              <div>
                <strong>Uklart</strong>
                <p>Skann meg</p>
              </div>

              <div>
                <strong>Bedre</strong>
                <p>Åpne den digitale menyen</p>
              </div>
            </div>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Kort overskrift.</li>
              <li className="text__seo-li">Stor QR-kode.</li>
              <li className="text__seo-li">Tydelig oppfordring.</li>
              <li className="text__seo-li">Restaurantlogo.</li>
              <li className="text__seo-li">Rolig bakgrunn.</li>
              <li className="text__seo-li">God kontrast.</li>
              <li className="text__seo-li">Ikke overfylt design.</li>
              <li className="text__seo-li">Eventuelt språk-hint.</li>
            </ul>

            <h3>Størrelse og skanneavstand betyr mye</h3>

            <p>
              For bordskilt er det bedre å ha nok visuell luft rundt QR-koden
              enn å planlegge helt på minimum. Gjester bør kunne skanne koden
              fra en naturlig sittende posisjon uten å måtte flytte kortet for
              mye.
            </p>
          </div>
        </section>

        <section id="placement" className="acquaintance">
          <h2>Hvor bør et QR-kode-bordskilt plasseres?</h2>

          <p>
            Plassering avgjør ofte om gjestene bruker QR-koden. Hvis
            bordskiltet står bak servietter, ved siden av en vase eller i
            skygge, kan gjestene overse det.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Gjør det synlig:</strong> Ikke skjul det mellom pynt,
              bestikk eller krydder.
            </li>

            <li className="text__seo-li">
              <strong>Ikke plasser det for lavt:</strong> QR-koden bør være
              enkel å skanne med en smarttelefon.
            </li>

            <li className="text__seo-li">
              <strong>Sjekk refleksjoner:</strong> Lamper, vinduer og stearinlys
              kan gjøre skanning vanskeligere.
            </li>

            <li className="text__seo-li">
              <strong>Vurder dobbeltsidig skilt:</strong> For større bord kan
              et dobbeltsidig bordskilt være nyttig.
            </li>

            <li className="text__seo-li">
              <strong>Test under ekte forhold:</strong> Ikke bare på kontoret,
              men ved et faktisk bord i restauranten.
            </li>
          </ol>

          <p>
            Et bordskilt bør ikke føles som reklame som står i veien. Det bør
            føles som en naturlig del av bordservicen.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvilke feil skjer med QR-kode-bordskilt?</h2>

            <p>
              Mange problemer oppstår når bordskiltet bare planlegges som et
              trykkprodukt, og ikke som en del av gjestens opplevelse.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Vanlige feil</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    QR-koden trykkes før menyen er testet.
                  </li>
                  <li className="text__seo-li">
                    Koden er for liten eller for sterkt designet.
                  </li>
                  <li className="text__seo-li">
                    Blank overflate reflekterer restaurantlys.
                  </li>
                  <li className="text__seo-li">
                    Bordskiltet står på et dårlig sted.
                  </li>
                  <li className="text__seo-li">
                    Teksten forklarer ikke formålet.
                  </li>
                  <li className="text__seo-li">
                    QR-koden åpner en gammel PDF.
                  </li>
                  <li className="text__seo-li">
                    Det finnes ingen erstatningskort for skadde bordskilt.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Bedre</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Test den digitale menyen først.
                  </li>
                  <li className="text__seo-li">
                    Trykk en stor QR-kode med god kontrast.
                  </li>
                  <li className="text__seo-li">
                    Sjekk materialet under ekte belysning.
                  </li>
                  <li className="text__seo-li">
                    Test plasseringen ved bordet.
                  </li>
                  <li className="text__seo-li">
                    Skriv tydelig: «Åpne den digitale menyen».
                  </li>
                  <li className="text__seo-li">Bruk en stabil menylenke.</li>
                  <li className="text__seo-li">
                    Ha noen erstatningskort klare.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>Hvordan ville jeg startet som restauranteier?</h2>

          <p>
            Jeg ville ikke bestilt en stor mengde profesjonelle bordskilt med en
            gang. Jeg ville startet med en liten test.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Forbered den digitale menyen:</strong> Sjekk menyen på en
              smarttelefon.
            </li>

            <li className="text__seo-li">
              <strong>Test QR-koden:</strong> Åpne den med iPhone og Android.
            </li>

            <li className="text__seo-li">
              <strong>Lag 2 til 3 test-bordskilt:</strong> Enkle, men lette å
              lese.
            </li>

            <li className="text__seo-li">
              <strong>Plasser dem i restauranten:</strong> Ved ekte bord og
              under ekte lys.
            </li>

            <li className="text__seo-li">
              <strong>Spør personalet:</strong> Hvilke spørsmål stiller
              gjestene?
            </li>

            <li className="text__seo-li">
              <strong>Produser endelig versjon senere:</strong> Bestill større
              mengde først etter testen.
            </li>
          </ol>

          <p>
            Dette reduserer risikoen for å ha pene bordskilt som ikke fungerer
            godt i daglig restaurantbruk.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hva bør du gjøre nå?</h2>

            <p>
              Hvis du vil lage QR-kode-bordskilt for restauranten din, start med
              en enkel praktisk test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">
                Opprett en digital meny først.
              </li>
              <li className="text__seo-li">
                Åpne menyen på en smarttelefon.
              </li>
              <li className="text__seo-li">
                Lag en QR-kode med en stabil lenke.
              </li>
              <li className="text__seo-li">Trykk 2 til 3 testkort.</li>
              <li className="text__seo-li">
                Plasser dem ved ekte bord.
              </li>
              <li className="text__seo-li">
                Sjekk lys, skanning og tydelighet.
              </li>
              <li className="text__seo-li">
                Bestill den endelige versjonen først etter testen.
              </li>
            </ul>

            <VisualIdea>
              En liten bordskilt-test: én mobil meny, én stabil QR-kode, to
              eller tre trykte kort og ekte bordplassering før endelig
              produksjon.
            </VisualIdea>
          </div>
        </section>

        <SeoWorkSteps lang="no" />

        <section id="faq" className="acquaintance">
          <h2>FAQ om QR-kode-bordskilt</h2>

          <div className="seoFaq">
            <details>
              <summary>Hva er et QR-kode-bordskilt?</summary>
              <p>
                Det er et lite kort eller en holder som plasseres på
                restaurantbordet og lar gjester åpne den digitale menyen ved å
                skanne en QR-kode.
              </p>
            </details>

            <details>
              <summary>Hva bør et bordskilt inneholde?</summary>
              <p>
                En stor QR-kode, en tydelig tekst som «Åpne den digitale menyen»
                og eventuelt logo eller språk-hint.
              </p>
            </details>

            <details>
              <summary>
                Må jeg trykke QR-koden på nytt når menyen endres?
              </summary>
              <p>
                Nei, ikke hvis QR-koden peker til en stabil menylenke.
                Menyinnholdet kan oppdateres online.
              </p>
            </details>

            <details>
              <summary>Hvilket materiale er best?</summary>
              <p>
                Til testing er laminerte kort eller enkle holdere nok. Til
                langvarig bruk er stabile materialer som er enkle å rengjøre
                bedre.
              </p>
            </details>

            <details>
              <summary>Er et klistremerke bedre enn et bordskilt?</summary>
              <p>
                Til testing er et bordskilt mer fleksibelt fordi det kan
                flyttes, byttes eller redesignes enklere.
              </p>
            </details>

            <details>
              <summary>Kan jeg lage QR-kode-bordskilt selv?</summary>
              <p>
                Ja. Du kan skrive ut kort, laminere dem eller sette dem i enkle
                holdere. Det viktigste er at QR-koden forblir enkel å skanne.
              </p>
            </details>
          </div>
        </section>

        <section className="articls qrArticle__related">
          <h2>Flere temaer om QR-menyer</h2>

          <div className="seoCompareSmall">
            <p>
              <Link to="/no/qr-kode-meny-uten-app">
                QR-kode-meny uten app
              </Link>
            </p>

            <p>
              <Link to="/no/digital-meny-for-restauranter">
                Digital meny for restauranter
              </Link>
            </p>

            <p>
              <Link to="/no/meny-med-bilder">
                Meny med bilder
              </Link>
            </p>

            <p>
              <Link to="/no/test-gratis-digital-meny">
                Test en gratis digital meny
              </Link>
            </p>
          </div>
        </section>
      </article>
    </main>
  );
}